import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Driver,
  getListDriversV3,
  type TalentType,
} from '@/services/api/drivers';

import { filterExpressRiders } from './riderFilter';

/**
 * Backs the rider `<SearchableDropdown>` (O4) across Scheduled Instant —
 * create-slot, standby, day-off, and the tabbed rider picker. Debounced server
 * search against `GET /v3/drivers` (matches name / code / phone), page-1 on
 * every query change, lazy `loadMore()`. Mirrors `useProvidersDropdown`.
 *
 * Options:
 *   type       — server-side TalentType filter. 4W needs a truck DRIVER, not
 *                a 2W RIDER, and the directory holds both.
 *   tagNames   — server-side tag filter (e.g. 'Scheduled Instant' → Dedicated).
 *   excludeTag — client-side drop of a tag (e.g. 'Express' on the All tab, D2).
 *                driver-service has no exclude filter yet (TODO T-DRV), so we
 *                drop them here, surface `hiddenCount`, and auto-advance once
 *                when a page comes back fully hidden so the list isn't empty.
 */

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

// Only offer riders who can actually be assigned. Excludes SUSPEND /
// BLACKLISTED / RESIGN / WITHDRAWN / ONBOARDING / REGISTERED / DAY_OFF —
// driver-service filters on `status IN (...)`. (DriverStatus enum, nest-driver-service.)
const ASSIGNABLE_STATUSES = 'ACTIVE,READY_TO_WORK';

function normalizeSearch(s: string): string {
  return /^0\d{6,}$/.test(s) ? `62${s.slice(1)}` : s;
}

export interface UseDriversDropdownOptions {
  tagNames?: string;
  excludeTag?: string;
  /** Narrow to one talent class; omit to keep every type (existing callers). */
  type?: TalentType;
}

export interface UseDriversDropdownResult {
  options: Driver[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  /** Drivers dropped by the client-side excludeTag filter (caveat text). */
  hiddenCount: number;
  setSearch: (value: string) => void;
  loadMore: () => void;
}

export function useDriversDropdown(
  open: boolean,
  opts: UseDriversDropdownOptions = {}
): UseDriversDropdownResult {
  const { tagNames, excludeTag, type } = opts;
  const [options, setOptions] = useState<Driver[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [search, setSearch] = useState('');

  const apply = useCallback(
    (raw: Driver[]) =>
      excludeTag
        ? filterExpressRiders(raw, excludeTag)
        : { visible: raw, hiddenCount: 0 },
    [excludeTag]
  );

  useEffect(() => {
    if (!open) {
      setSearch('');
      setOptions([]);
      setPage(1);
      setHasMore(false);
      setHiddenCount(0);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const trimmed = search.trim();
    let cancelled = false;

    const fire = () => {
      setLoading(true);
      setError(null);
      getListDriversV3({
        page: '1',
        size: String(PAGE_SIZE),
        search: trimmed ? normalizeSearch(trimmed) : undefined,
        tagNames,
        type,
        statuses: ASSIGNABLE_STATUSES,
      })
        .then((res) => {
          if (cancelled) return;
          const { visible, hiddenCount: hc } = apply(res.data ?? []);
          setOptions(visible);
          setHiddenCount(hc);
          setPage(1);
          setHasMore((res.pagination?.lastPage ?? 0) > 1);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : 'Gagal memuat rider');
          setOptions([]);
          setHasMore(false);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (trimmed) timer = setTimeout(fire, SEARCH_DEBOUNCE_MS);
    else fire();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, search, tagNames, type, apply]);

  const loadMore = useCallback(() => {
    if (!open || loading || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    const trimmed = search.trim();
    setLoadingMore(true);
    getListDriversV3({
      page: String(nextPage),
      size: String(PAGE_SIZE),
      search: trimmed ? normalizeSearch(trimmed) : undefined,
      tagNames,
      type,
      statuses: ASSIGNABLE_STATUSES,
    })
      .then((res) => {
        const { visible, hiddenCount: hc } = apply(res.data ?? []);
        setOptions((prev) => [...prev, ...visible]);
        setHiddenCount((prev) => prev + hc);
        setPage(nextPage);
        setHasMore(nextPage < (res.pagination?.lastPage ?? 0));
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Gagal memuat rider');
        setHasMore(false);
      })
      .finally(() => setLoadingMore(false));
  }, [
    open,
    loading,
    loadingMore,
    hasMore,
    page,
    search,
    tagNames,
    type,
    apply,
  ]);

  // Empty-page footgun (D2/A7): a page filtered fully to Express leaves the
  // list empty while more pages exist — auto-advance once per such page so the
  // user isn't stuck looking at nothing. Bounded by hasMore + loading guards.
  const autoAdvancedFor = useRef(-1);
  useEffect(() => {
    if (
      open &&
      excludeTag &&
      !loading &&
      !loadingMore &&
      hasMore &&
      options.length === 0 &&
      autoAdvancedFor.current !== page
    ) {
      autoAdvancedFor.current = page;
      loadMore();
    }
  }, [
    open,
    excludeTag,
    loading,
    loadingMore,
    hasMore,
    options.length,
    page,
    loadMore,
  ]);

  return {
    options,
    loading,
    loadingMore,
    hasMore,
    error,
    hiddenCount,
    setSearch,
    loadMore,
  };
}
